import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  InputGroup,
  useToast,
  Text,
  FormControl,
  Switch,
  Box,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { InputField } from '@/components/InputField';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ImageUpload } from '@/components/ImageUpload';
import { TextareaField } from '@/components/TextareaField';
import { PostNewsCalendarEventsRequest } from '@/services/request/calendar-events';
import { usePostCalendarEventsMutation } from '@/services/queries/calendar-events';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';
import { SelectField } from '@/components/SelectField';
import { HyperlinkTarget } from '@/model';

export interface CalendarEventCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export const CalendarEventCreateModal = ({ isOpen, onClose, onDone }: CalendarEventCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const postItemMutation = usePostCalendarEventsMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
    watch,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      team_uuid: '',
      name: '',
      place: '',
      event_at: '',
      is_display: false,
      hyperlink: '',
      hyperlink_target: HyperlinkTarget.EXTERNAL,
      description: '',
      file: undefined,
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formValues = watch();
  const hasTicketLink = !!formValues.hyperlink?.trim();

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload: PostNewsCalendarEventsRequest = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        name: data.name,
        place: data.place,
        event_at: data.event_at,
        is_display: data.is_display,
        hyperlink: data.hyperlink,
        hyperlink_target: data.hyperlink_target,
        description: data.description,
        image: data.file,
      };

      postItemMutation.mutate(payload, {
        onSuccess: () => {
          onDone?.();
          reset();
          toast({
            status: 'success',
            title: t('pages.application_calendar_events.components.calendar_create_modal.success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.application_calendar_events.components.calendar_create_modal.error'),
          });
        },
      });
    },
    [onDone, postItemMutation, reset, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_calendar_events.components.calendar_create_modal.title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_calendar_events.components.calendar_create_modal.team_label')}
              placeholder={t('pages.application_calendar_events.components.calendar_create_modal.team_label')}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <InputField
              withoutFloating
              label={t('pages.application_calendar_events.components.calendar_create_modal.date_label')}
              type='datetime-local'
              fullDatePickerArea
              rootProps={{ isInvalid: !!errors?.event_at, errorMessage: errors?.event_at?.message }}
              {...register('event_at')}
            />
            <Flex direction='row' gap={6} width='100%'>
              <InputField
                withoutFloating
                label={t('pages.application_calendar_events.components.calendar_create_modal.event_name_label')}
                {...register('name')}
                helperText={t('pages.application_calendar_events.components.calendar_create_modal.event_name_helper')}
                maxLength={100}
                rootProps={{ isInvalid: !!errors?.name, errorMessage: errors?.name?.message }}
                flex='1'
              />
              <InputField
                withoutFloating
                label={t('pages.application_calendar_events.components.calendar_create_modal.place_label')}
                {...register('place')}
                helperText={t('pages.application_calendar_events.components.calendar_create_modal.place_helper')}
                maxLength={100}
                rootProps={{ isInvalid: !!errors?.place, errorMessage: errors?.place?.message }}
                flex='1'
              />
            </Flex>
            <FormControl display='flex' gap={2} alignItems='center'>
              <Text fontWeight='medium'>
                {t('pages.application_calendar_events.components.calendar_create_modal.display_label')}
              </Text>
              <Switch
                name='is_display'
                isChecked={formValues.is_display}
                onChange={event => {
                  setValue('is_display', event.target.checked);
                }}
              />
            </FormControl>
            <Box borderWidth='1px' borderColor='gray.200' borderRadius={8} p={4}>
              <Flex direction='column' gap={4}>
                <Text fontWeight='bold'>{t('pages.application_calendar_events.components.calendar_create_modal.tickets_section_title')}</Text>

                <InputField
                  withoutFloating
                  label={t('pages.application_calendar_events.components.calendar_create_modal.ticket_link_label')}
                  {...register('hyperlink')}
                  helperText={t('pages.application_calendar_events.components.calendar_create_modal.ticket_link_helper')}
                  maxLength={255}
                  rootProps={{ isInvalid: !!errors?.hyperlink, errorMessage: errors?.hyperlink?.message }}
                />

                {/* Config dependente do link: sem link não há o que abrir. */}
                <FormControl
                  display='flex'
                  flexDirection='column'
                  gap={2}
                  pl={4}
                  borderLeft='2px solid'
                  borderColor='gray.200'
                  isDisabled={!hasTicketLink}
                >
                  <Text fontWeight='medium'>{t('pages.application_calendar_events.components.calendar_create_modal.ticket_link_target_label')}</Text>
                  <SelectField {...register('hyperlink_target')} isDisabled={!hasTicketLink}>
                    <option value={HyperlinkTarget.EXTERNAL}>{t('pages.application_calendar_events.components.calendar_create_modal.ticket_link_target_external')}</option>
                    <option value={HyperlinkTarget.INTERNAL}>{t('pages.application_calendar_events.components.calendar_create_modal.ticket_link_target_internal')}</option>
                    <option value={HyperlinkTarget.PARTNER}>{t('pages.application_calendar_events.components.calendar_create_modal.ticket_link_target_partner')}</option>
                  </SelectField>
                  <Text fontSize='sm' color='gray.500'>
                    {hasTicketLink
                      ? t('pages.application_calendar_events.components.calendar_create_modal.ticket_link_target_helper')
                      : t('pages.application_calendar_events.components.calendar_create_modal.ticket_link_target_helper_empty')}
                  </Text>
                </FormControl>
              </Flex>
            </Box>
            <FormControl display='flex' flexDirection='column' gap={2} isInvalid={!!errors?.description}>
              <Text fontWeight='medium'>
                {t('pages.application_calendar_events.components.calendar_create_modal.description_label')}
              </Text>
              <TextareaField
                maxLength={1000}
                helperText={t('pages.application_calendar_events.components.calendar_create_modal.description_helper')}
                error={errors?.description?.message}
                {...register('description')}
              />
            </FormControl>
            <InputGroup display='flex' flexDirection='column' width='100%'>
              <ImageUpload
                label={t('pages.application_calendar_events.components.calendar_create_modal.image_label')}
                imageURL={''}
                onFileChange={file => setValue('file', file)}
                helpText={t('pages.application_calendar_events.components.calendar_create_modal.image_helper')}
                error={errors?.file?.message?.toString()}
              />
            </InputGroup>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w='100%' justifyContent={'space-between'} alignItems='center' gap={2} paddingLeft={6}>
            <Flex w={'100%'} justifyContent='end' gap={5}>
              <Button variant='outlined' color='gray.600' onClick={onClose}>
                {t('pages.application_calendar_events.components.calendar_create_modal.cancel')}
              </Button>
              <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
                {t('pages.application_calendar_events.components.calendar_create_modal.create')}
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={postItemMutation.isLoading} />
    </Modal>
  );
};
